import { createContext, useEffect, useReducer, useState } from "react";
import AlarmButton from "./Alarm";
import InterestButton from "./Interest";
import axios from "axios";
import { useSession } from "next-auth/react";
import CustomToast from "components/atoms/CustomToast";
import { useRouter } from "next/router";
import RiskAlarmNotiModal from "components/modals/RiskAlarmNotiModal";
import * as Sentry from "@sentry/nextjs";
import { useInterestToastDispatch } from "contexts/InterestContext";
import Button from "components/common/buttons/buttons";
import { useKBGlobalState } from "contexts/KBContext";
import { CRYPTO } from "utils/constant/ITME_INFO";
import DetailHeaderKBOrder from "./KBOrder";

export const DetailButtonsContext = createContext<any>(null);

interface State {
  clickCount: number;
  totalClicks: number;
  isBlocked: boolean;
}
type Action =
  | { type: "click"; button: number }
  | { type: "reset"; button: number }
  | { type: "block"; button: number }
  | { type: "unblock"; button: number };

const initialState: Record<number, State> = {
  1: { clickCount: 0, totalClicks: 0, isBlocked: false },
  2: { clickCount: 0, totalClicks: 0, isBlocked: false },
};

function reducer(
  state: Record<number, State>,
  action: Action
): Record<number, State> {
  switch (action.type) {
    case "click":
      return {
        ...state,
        [action.button]: {
          ...state[action.button],
          clickCount: state[action.button].clickCount + 1,
          totalClicks: state[action.button].totalClicks + 1,
        },
      };
    case "reset":
      return {
        ...state,
        [action.button]: { ...state[action.button], clickCount: 0 },
      };
    case "block":
      return {
        ...state,
        [action.button]: { ...state[action.button], isBlocked: true },
      };
    case "unblock":
      return {
        ...state,
        [action.button]: {
          ...state[action.button],
          isBlocked: false,
          clickCount: 0,
          totalClicks: 0,
        },
      };
    default:
      return state;
  }
}

export const AlarmProvider = ({
  children,
  detailData,
}: {
  children: (props: any) => React.ReactNode;
  detailData: any;
}) => {
  const router = useRouter();
  const { data: session, update }: any = useSession();
  const [isAlarmModalOpen, setIsAlarmModalOpen] = useState(false);

  const [modalTimer, setTimer] = useState<any>();

  //클릭 횟수 제한
  const [clickBlockToastVisible, setClickBlockToastVisible] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const globalInterestDispatch = useInterestToastDispatch();

  useEffect(() => {
    const timeouts = Object.keys(state).map((key) =>
      setTimeout(() => {
        dispatch({ type: "reset", button: parseInt(key) });
      }, 2500)
    );

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [state]);

  const sessionUpdate = async () => {
    let timer: NodeJS.Timeout;
    // timer 가 끝날 때까지 기다림 Promise
    await new Promise((resolve: any) => {
      timer = setTimeout(() => {
        update();
        resolve();
      }, 100);
      // clearTimeout(timer);
    });
  };

  const [isNotAgreeUser, setIsNotAgreeUser] = useState<any>();
  const [isRiskAlarmOn, setIsRiskAlarmOn] = useState<any>();

  const alarmRegister = async ({
    from,
    setAlarmSuccessToastVisible,
    fail,
    success,
  }: {
    from: "star" | "alarm" | "cta";
    setAlarmSuccessToastVisible?: any;
    fail?: (reason?: string) => void;
    success?: () => void;
  }) => {
    try {
      // 알림 수신 동의 여부 체크
      // 알림 수신 동의를 하지 않았고, 관심 자산에 등록을 했을 경우 알림 등록 모달

      let response;
      if (
        typeof isNotAgreeUser == "boolean" &&
        typeof isRiskAlarmOn == "boolean"
      ) {
        response = {
          isNotAgreeUser: isNotAgreeUser,
          isRiskAlarmOn: isRiskAlarmOn,
        };
      } else {
        response = await axios
          .get(
            session?.user?.email
              ? `/api/auth/alarm?email=${session.user.email}`
              : ""
          )
          .then((res) => res.data);
        setIsNotAgreeUser(response?.isNotAgreeUser === 1 ? 1 : 0);
        setIsRiskAlarmOn(response?.isRiskAlarmOn === 1 ? 1 : 0);
      }

      if (response?.isNotAgreeUser === 0) {
        // 알림 수신 동의를 하지 않았을 경우
        if (from === "star") {
          const timer = setTimeout(() => {
            const hideModal = localStorage.getItem("hideModal");
            const hideUntil = hideModal ? new Date(hideModal) : null;

            if (!hideUntil || hideUntil < new Date()) {
              // setIsAlarmModalOpen(true);
              globalInterestDispatch({
                type: "SET_ALARM_MODAL_VISIBLE",
                payload: {
                  alarmModal: true,
                  triggerSectionId: "stock_header",
                  ITEM_CD_DL: detailData.ITEM_CD_DL,
                  fail: fail,
                  success: success,
                },
              });
            }
          }, 1000);
          setTimer(timer);
        } else {
          //from === "alarm"
          // setAlarmRegister(true);

          setTimeout(() => {
            globalInterestDispatch({
              type: "SET_ALARM_MODAL_VISIBLE",
              payload: {
                alarmModal: true,
                triggerSectionId:
                  from === "cta"
                    ? "stock_banner_attractiveness_notify"
                    : "stock_header",
                ITEM_CD_DL: detailData?.ITEM_CD_DL,
                success: success,
                fail: fail,
              },
            });
          }, 100);
        }
      } else {
        if (from === "star" && response?.isRiskAlarmOn === 0) {
          return;
        }

        //알림등록
        await axios.post(session?.user?.email ? `/api/auth/alarm` : "", {
          email: session?.user?.email,
          ITEM_CD_DL: detailData.ITEM_CD_DL,
        });
        if (setAlarmSuccessToastVisible) {
          setAlarmSuccessToastVisible(true);
        }
        success && success();
      }
      update();
    } catch (e: any) {
      Sentry.captureException(e);
      fail && fail("backend_" + e?.response?.status);
      return new Error("Failed to register alarm");
    }
  };

  useEffect(() => {
    return () => {
      clearTimeout(modalTimer);
    };
  }, []);
  return (
    <DetailButtonsContext.Provider
      value={{
        dispatch,
        state,
        setClickBlockToastVisible,
        alarmRegister,
        session,
        update,
        sessionUpdate,
        detailData,
      }}
    >
      {children({
        dispatch,
        state,
        setClickBlockToastVisible,
        alarmRegister,
        session,
        update,
        sessionUpdate,
        detailData,
      })}
    </DetailButtonsContext.Provider>
  );
};

const Buttons = ({
  detailData,
  setAlarmRegister,
  isLoginUser,
  isMobile,
}: {
  detailData: any;
  setAlarmRegister: any;
  isLoginUser: boolean;
  isMobile: boolean;
}) => {
  const router = useRouter();
  const { data: session, update }: any = useSession();
  const [isAlarmModalOpen, setIsAlarmModalOpen] = useState(false);

  const [modalTimer, setTimer] = useState<any>();

  return (
    <AlarmProvider detailData={detailData}>
      {({
        dispatch,
        state,
        setClickBlockToastVisible,
        alarmRegister,
        session,
        update,
        sessionUpdate,
        detailData,
        clickBlockToastVisible,
      }: any) => {
        return (
          <>
            <div className="flex items-center">
              <InterestButton />
              <AlarmButton />
              <DetailHeaderKBOrder
                isLoginUser={isLoginUser}
                isMobile={isMobile}
                detailData={detailData}
              />
            </div>
            <CustomToast
              message={
                router.locale === "ko"
                  ? `짧은 시간에 너무 많이 반복했어요\n잠시 후 다시 시도해주세요.`
                  : `Too many tries in a short period.\n Please try again later.`
              }
              isVisible={clickBlockToastVisible}
              isSuccess={false}
              onClose={() => setClickBlockToastVisible(false)}
              width={router.locale === "ko" ? 275 : 300}
            />
            {isAlarmModalOpen && (
              <RiskAlarmNotiModal
                isModalOpen={isAlarmModalOpen}
                setModalOpen={setIsAlarmModalOpen}
                setAlarmRegister={setAlarmRegister}
              />
            )}
          </>
        );
      }}
    </AlarmProvider>
  );
};
export default Buttons;
